import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import apiClient, { calculateBundlePrice } from '../services/api';
import { useUserPreferences } from './UserPreferencesContext';

const PricingContext = createContext();

export const PricingProvider = ({ children, fallbackRegion = 'eu' }) => {
  // Try to use UserPreferencesProvider if available, otherwise use fallback
  let region = fallbackRegion;
  try {
    const userPrefs = useUserPreferences();
    region = userPrefs.region;
  } catch (error) {
    // useUserPreferences is not available (e.g., in ComparisonPage)
    // Use fallback region or get from localStorage
    region = localStorage.getItem('region') || fallbackRegion;
  }
  const [currencyRates, setCurrencyRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Regional pricing data (will be loaded from backend later)
  const [regionalPrices] = useState({
    business_phone: {
      regions: {
        ua: { base_price: 10.0, currency: "USD" },
        eu: { base_price: 10.0, currency: "USD" },
        asia: { base_price: 13.0, currency: "USD" },
        other: { base_price: 20.0, currency: "USD" }
      }
    },
    business_phone_user: {
      regions: {
        ua: { base_price: 10.0, currency: "USD" },
        eu: { base_price: 10.0, currency: "USD" },
        asia: { base_price: 13.0, currency: "USD" },
        other: { base_price: 20.0, currency: "USD" }
      }
    },
    call_tracking_session: {
      regions: {
        ua: { base_price: 0.05, currency: "USD" },
        eu: { base_price: 0.05, currency: "USD" },
        asia: { base_price: 0.065, currency: "USD" },
        other: { base_price: 0.10, currency: "USD" }
      }
    },
    ai_call_minute: {
      regions: {
        ua: { base_price: 0.15, currency: "USD" },
        eu: { base_price: 0.15, currency: "USD" },
        asia: { base_price: 0.20, currency: "USD" },
        other: { base_price: 0.30, currency: "USD" }
      }
    },
    callback_session: {
      regions: {
        ua: { base_price: 0.08, currency: "USD" },
        eu: { base_price: 0.08, currency: "USD" },
        asia: { base_price: 0.10, currency: "USD" },
        other: { base_price: 0.15, currency: "USD" }
      }
    },
    voice_traffic_minute: {
      regions: {
        ua: { base_price: 0.02, currency: "USD" },
        eu: { base_price: 0.02, currency: "USD" },
        asia: { base_price: 0.025, currency: "USD" },
        other: { base_price: 0.04, currency: "USD" }
      }
    },
    phone_number: {
      regions: {
        ua: { base_price: 3.0, currency: "USD" },
        eu: { base_price: 3.0, currency: "USD" },
        asia: { base_price: 4.0, currency: "USD" },
        other: { base_price: 6.0, currency: "USD" }
      }
    }
  });

  // Load currency rates from public display endpoint
  useEffect(() => {
    const loadCurrencyRates = async () => {
      try {
        setLoading(true);
        // Use public display-rates endpoint (no auth required)
        const response = await apiClient.get('/currency/display-rates');
        
        const data = response.data;
        setCurrencyRates(data);
        setError(null);
      } catch (err) {
        console.error('Failed to load currency rates:', err);
        setError(err);
        
        // NO FALLBACK! Let the error propagate to UI
        setCurrencyRates(null);
      } finally {
        setLoading(false);
      }
    };

    loadCurrencyRates();
  }, []);

  // Get price for specific product and region
  const getPriceForRegion = useCallback((productId, targetRegion) => {
    const product = regionalPrices[productId];
    if (!product || !product.regions[targetRegion]) {
      return 0;
    }
    return product.regions[targetRegion].base_price;
  }, [regionalPrices]);

  // Calculate product price with business logic using backend API
  const calculateProductPrice = useCallback(async (productCalculationRequest) => {
    try {
      const response = await apiClient.post('/pricing/calculate', productCalculationRequest);
      return response.data.pricing;
    } catch (error) {
      console.error('Failed to calculate product price:', error);
      // Fallback to local calculation
      return getDisplayPriceFallback(productCalculationRequest.product_id, productCalculationRequest.quantity, productCalculationRequest.region);
    }
  }, []);

  // Calculate total price for multiple products using backend API
  const calculateComplexTotal = useCallback(async (region, products) => {
    try {
      const response = await apiClient.post('/pricing/calculate-total', {
        region: region,
        products: products
      });
      return response.data.total;
    } catch (error) {
      console.error('Failed to calculate complex total:', error);
      // Fallback to local calculation
      return calculateTotalFallback(products.reduce((acc, product) => {
        acc[product.product_id] = product.quantity;
        return acc;
      }, {}), region);
    }
  }, []);

  // Calculate bundle pricing with discounts using backend API
  const calculateBundle = useCallback(async (bundleRequest) => {
    try {
      const response = await calculateBundlePrice(bundleRequest);
      return response.data;
    } catch (error) {
      console.error('Failed to calculate bundle pricing:', error);
      throw error; // Re-throw to let caller handle fallback
    }
  }, []);

  // Fallback method for individual product pricing (legacy)
  const getDisplayPriceFallback = useCallback((productId, quantity = 1, targetRegion = null) => {
    const useRegion = targetRegion || region;
    const basePrice = getPriceForRegion(productId, useRegion);
    const totalPrice = basePrice * quantity;

    if (useRegion === 'ua') {
      // For Ukraine, show only USD for individual products
      return {
        primary: {
          amount: totalPrice,
          currency: 'USD',
          symbol: '$'
        }
      };
    } else if (useRegion === 'eu') {
      // For Europe, show EUR
      return {
        primary: {
          amount: totalPrice,
          currency: 'EUR',
          symbol: '€'
        }
      };
    } else {
      // For Asia and Other, show USD
      return {
        primary: {
          amount: totalPrice,
          currency: 'USD',
          symbol: '$'
        }
      };
    }
  }, [region, regionalPrices, getPriceForRegion]);

  // Get display price with currency conversion (updated to use backend)
  const getDisplayPrice = useCallback(async (productId, quantity = 1, targetRegion = null) => {
    const useRegion = targetRegion || region;
    
    // Use backend API for complex calculations
    const productRequest = {
      product_id: productId,
      region: useRegion,
      quantity: quantity,
      apply_annual_discount: false
    };

    return await calculateProductPrice(productRequest);
  }, [region, calculateProductPrice]);

  // Get currency symbol for region
  const getCurrencySymbol = useCallback((targetRegion = null) => {
    const useRegion = targetRegion || region;
    switch (useRegion) {
      case 'ua': return '$'; // Primary currency for Ukraine is USD
      case 'eu': return '€';
      default: return '$';
    }
  }, [region]);

  // Convert USD to UAH
  const convertUSDToUAH = useCallback((usdAmount) => {
    return usdAmount * (currencyRates.usd || 40.0);
  }, [currencyRates]);

  // Calculate total for multiple products (fallback method)
  const calculateTotalFallback = useCallback((products, targetRegion = null) => {
    const useRegion = targetRegion || region;
    let totalUSD = 0;

    // Calculate total in USD first
    Object.entries(products).forEach(([productId, quantity]) => {
      const price = getPriceForRegion(productId, useRegion);
      totalUSD += price * quantity;
    });

    // For Monthly Cost, show UAH for Ukraine region with "грн" symbol
    if (useRegion === 'ua') {
      // For Ukraine Monthly Cost, show both USD and UAH
      const uahAmount = totalUSD * (currencyRates.usd || 40.0);
      return {
        primary: {
          amount: totalUSD,
          currency: 'USD',
          symbol: '$'
        },
        secondary: {
          amount: Math.round(uahAmount),
          currency: 'UAH',
          symbol: 'грн' // Changed from ₴ to грн
        }
      };
    } else if (useRegion === 'eu') {
      // For Europe, show EUR
      return {
        primary: {
          amount: totalUSD,
          currency: 'EUR',
          symbol: '€'
        }
      };
    } else {
      // For Asia and Other, show USD
      return {
        primary: {
          amount: totalUSD,
          currency: 'USD',
          symbol: '$'
        }
      };
    }
  }, [region, getPriceForRegion, currencyRates]);

  // Calculate total for multiple products (updated to use backend)
  const calculateTotal = useCallback(async (products, targetRegion = null) => {
    const useRegion = targetRegion || region;
    
    // Convert simple products object to backend format
    const productsArray = Object.entries(products).map(([productId, quantity]) => ({
      product_id: productId,
      region: useRegion,
      quantity: quantity
      // Removed apply_annual_discount - discounts now applied on frontend
    }));

    return await calculateComplexTotal(useRegion, productsArray);
  }, [region, calculateComplexTotal]);

  // Apply annual discount (17% discount = divide by 1.2)
  const applyAnnualDiscount = useCallback((price, productId) => {
    // Products that don't receive annual discount
    const NO_ANNUAL_DISCOUNT_PRODUCTS = ['phone-numbers', 'voice-traffic', 'ai-conversation'];
    
    if (NO_ANNUAL_DISCOUNT_PRODUCTS.includes(productId)) {
      return price;
    }
    
    return price / 1.2; // 17% discount
  }, []);

  // Apply subscription discounts (bonuses for subscription periods)
  const applySubscriptionDiscount = useCallback((price, productId, subscriptionType) => {
    // Products eligible for subscription bonus
    const BONUS_ELIGIBLE_PRODUCTS = ['business-phone', 'fmc', 'call-tracking', 'callback', 'omnichannel-chat', 'ai-conversation'];
    
    if (!BONUS_ELIGIBLE_PRODUCTS.includes(productId)) {
      return price; // No subscription bonus for non-eligible products
    }
    
    switch (subscriptionType) {
      case '360-days':
        return price / 1.2; // 20% subscription bonus (≈16.67% discount)
      case '180-days':
        return price / 1.1;  // ≈9.09% subscription bonus
      case '90-days':
      case '12-month-contract':
      case '360-days-split':
      default:
        return price; // No subscription bonus
    }
  }, []);

  const contextValue = {
    regionalPrices,
    currencyRates,
    loading,
    error,
    getPriceForRegion,
    getDisplayPrice,
    getDisplayPriceFallback, // Legacy fallback method
    getCurrencySymbol,
    convertUSDToUAH,
    calculateTotal,
    calculateTotalFallback, // Legacy fallback method
    calculateProductPrice, // New backend API method
    calculateComplexTotal, // New backend API method
    calculateBundle, // New bundle pricing method
    applyAnnualDiscount, // New discount methods
    applySubscriptionDiscount,
    region // Current user's region
  };

  return (
    <PricingContext.Provider value={contextValue}>
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = () => {
  const context = useContext(PricingContext);
  if (context === undefined) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return context;
};

export default PricingContext;